import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dataApi, fetchMediaBlob, mediaUrl } from '@/lib/api';
import { isNative } from '@/lib/platform';
import type { MediaAsset } from '@/types/api';
import { translate, useLocale, useT } from '@/lib/i18n';
import { PhotoLightbox } from './PhotoLightbox';
import { MediaAnalysisPanel } from './MediaAnalysisPanel';

export function MediaAttachments({
  familyId,
  ids,
  light = false,
  imagesOnly = false,
  horizontal = false,
  onRemove,
}: {
  familyId: string;
  ids: string[];
  light?: boolean;
  imagesOnly?: boolean;
  horizontal?: boolean;
  onRemove?: (mediaId: string) => void;
}) {
  const t = useT();
  const [documentAsset, setDocumentAsset] = useState<MediaAsset | null>(null);
  const media = useQuery({
    queryKey: ['media', familyId],
    queryFn: () => dataApi.media(familyId),
    enabled: ids.length > 0,
  });

  if (ids.length === 0) return null;

  const assets = ids
    .map((id) => media.data?.find((asset) => asset.id === id))
    .filter((asset): asset is MediaAsset => Boolean(asset))
    .filter((asset) => !imagesOnly || asset.mime.startsWith('image/'));

  if (!media.data) {
    return (
      <p className={`mt-2 text-xs ${light ? 'text-white/70' : 'text-heritage-400'}`}>
        {t('Загрузка вложений…')}
      </p>
    );
  }

  if (assets.length === 0) return null;
  const scrollable = horizontal && assets.length > 1;

  return (
    <div
      className={`mt-2 flex gap-2 ${
        scrollable ? 'snap-x overflow-x-auto pb-1' : 'flex-wrap'
      }`}
    >
      {assets.map((asset) => {
        const isImage = asset.mime.startsWith('image/');
        return (
          <div
            key={asset.id}
            className={`relative ${scrollable ? 'shrink-0 snap-start' : ''}`}
          >
            {isImage ? (
              <MediaImage
                familyId={familyId}
                asset={asset}
                light={light}
                compact={scrollable}
              />
            ) : (
              <button
                type="button"
                onClick={() => setDocumentAsset(asset)}
                className={`rounded-lg px-2.5 py-1.5 text-xs underline ${
                  light
                    ? 'bg-white/15 text-white'
                    : 'bg-heritage-100 text-heritage-700'
                }`}
              >
                {t('Документ · {size} КБ', {
                  size: (asset.size / 1024).toFixed(0),
                })}
              </button>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(asset.id)}
                className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-surface text-sm font-semibold text-heritage-600 shadow ring-1 ring-heritage-200 hover:text-red-600"
                title={t('Удалить вложение')}
                aria-label={t('Удалить вложение')}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      {documentAsset && (
        <DocumentAnalysisModal
          familyId={familyId}
          asset={documentAsset}
          onClose={() => setDocumentAsset(null)}
        />
      )}
    </div>
  );
}

function DocumentAnalysisModal({
  familyId,
  asset,
  onClose,
}: {
  familyId: string;
  asset: MediaAsset;
  onClose: () => void;
}) {
  const t = useT();
  const analysis = useQuery({
    queryKey: ['media-analysis', familyId, asset.id],
    queryFn: () => dataApi.mediaAnalysis(familyId, asset.id),
  });
  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[85dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-heritage-100 px-4 py-3">
          <h2 className="truncate font-semibold">
            {asset.originalName ?? t('Документ')}
          </h2>
          <button type="button" onClick={onClose} className="btn-secondary px-3 py-1.5 text-xs">
            {t('Закрыть')}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {analysis.isLoading && (
            <p className="text-sm text-heritage-500">{t('Распознаём документ…')}</p>
          )}
          {analysis.isError && (
            <p className="text-sm text-red-700">{t('Не удалось распознать текст.')}</p>
          )}
          {analysis.data?.ocrText ? (
            <>
              <p className="mb-2 text-xs uppercase tracking-wide text-heritage-500">
                {t('Распознанный текст')}
              </p>
              <pre className="whitespace-pre-wrap break-words font-sans text-sm text-heritage-800">
                {analysis.data.ocrText}
              </pre>
            </>
          ) : (
            !analysis.isLoading && (
              <p className="text-sm text-heritage-500">{t('Текст не найден.')}</p>
            )
          )}
        </div>
        <div className="flex justify-end border-t border-heritage-100 p-3">
          <button
            type="button"
            onClick={() => void openDocument(familyId, asset)}
            className="btn-primary px-3 py-1.5 text-xs"
          >
            {t('Открыть файл')}
          </button>
        </div>
      </div>
    </div>
  );
}

async function openDocument(familyId: string, asset: MediaAsset) {
  if (!isNative()) {
    window.open(mediaUrl(familyId, asset.id), '_blank', 'noopener');
    return;
  }

  const blob = await fetchMediaBlob(familyId, asset.id);
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');
  const extension = asset.s3Key.split('.').pop() ?? 'bin';
  const saved = await Filesystem.writeFile({
    path: `heritage/${asset.id}.${extension}`,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });
  await Share.share({
    title: translate(useLocale.getState().locale, 'Документ Семьи P&Co'),
    url: saved.uri,
  });
}

function MediaImage({
  familyId,
  asset,
  light,
  compact,
}: {
  familyId: string;
  asset: MediaAsset;
  light: boolean;
  compact: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(
    isNative() ? null : mediaUrl(familyId, asset.id),
  );

  useEffect(() => {
    if (!isNative()) return;
    let url: string | null = null;
    let alive = true;
    void fetchMediaBlob(familyId, asset.id)
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [asset.id, familyId]);

  if (!src) {
    return (
      <span
        className={`rounded-lg px-2.5 py-1.5 text-xs ${
          light ? 'bg-white/15 text-white' : 'bg-heritage-100 text-heritage-600'
        }`}
      >
        {t('Фото')}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block cursor-zoom-in"
        aria-label={t('Открыть фото')}
        title={t('Открыть фото')}
      >
        <img
          src={src}
          alt={t('Семейное фото')}
          className={`rounded-xl object-cover ${
            compact ? 'h-32 w-32' : 'max-h-44 max-w-full'
          }`}
          loading="lazy"
        />
      </button>
      {open && (
        <PhotoLightbox
          src={src}
          alt={t('Семейное фото')}
          details={
            <MediaAnalysisPanel familyId={familyId} mediaId={asset.id} />
          }
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
